import { ChangeDetectionStrategy, Component, computed, input, Input, model, signal, ViewChild } from '@angular/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { DxPopoverComponent, DxPopoverModule } from 'devextreme-angular/ui/popover';
import { DxListModule } from 'devextreme-angular/ui/list';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { CommonModule } from '@angular/common';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxRadioGroupModule } from 'devextreme-angular/ui/radio-group';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';

@Component({
    selector: 'gpd-filter',
    imports: [
        CommonModule,
        SvgIconComponent,
        DxPopoverModule,
        DxListModule,
        DxCheckBoxComponent,
        DxTextBoxComponent,
        DxRadioGroupModule,
        GpdButtonComponent,
    ],
    templateUrl: './gpd-filter.html',
    styleUrl: './gpd-filter.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GpdFilter {
  @ViewChild('popover') popover!: DxPopoverComponent;

  options = input<any[]>([]);
  value = model<any[]>([]);

  @Input() text = '';
  @Input() valueExpr = 'value';
  @Input() labelExpr = 'label';
  @Input() subtitleExpr = '';
  @Input() multiple = true;
  @Input() nullable = true;
  @Input() disableAll = false;
  @Input() disableSearch = false;
  @Input() activeWhenNotEmpty = false;
  @Input() baselineValue: any[] | null = null;
  @Input() inactiveWhenEmpty = false;
  // Never paints itself active. For a pill that picks a display mode rather than narrowing
  // rows, where every option is equally "on" and none of them is a filter being applied.
  @Input() neverActive = false;
  // Renders the trigger as a compact square filter-glyph button instead of the
  // default text+chevron pill — for toolbars where the label would be redundant.
  @Input() iconOnly = false;

  popoverVisible = signal(false);

  filterCount = computed(() => {
    const val = this.value();
    return Array.isArray(val) ? val.length : 0;
  });

  isActive = computed(() => {
    const opts = this.options();
    const val = this.value();
    // A display mode rather than a filter: it holds a value at all times and narrows nothing,
    // so there is no "on" for it to signal and no neutral position to compare against.
    if (this.neverActive) return false;
    if (this.activeWhenNotEmpty) return val.length > 0;
    if (this.inactiveWhenEmpty) return val.length > 0 && val.length !== opts.length;
    return opts.length > 0 && val.length !== opts.length;
  });

  protected searchTerm = signal<string>('');

  filteredOptions = computed(() => {
    const searchTerm = this.searchTerm();
    if (searchTerm === '') return this.options();
    return this.options().filter(o => o[this.labelExpr].toLowerCase().includes(searchTerm.toLowerCase()));
  });

  isSelected(item: any): boolean {
    const val = this.value();
    return Array.isArray(val) && val.includes(item[this.valueExpr]);
  }

  isReadOnly(item: any): boolean {
    if (this.nullable || this.multiple) return false;
    return this.isSelected(item) && this.value().length === 1;
  }

  togglePopover() {
    console.log(this.options());

    this.popoverVisible.update(v => !v);
  }

  onOptionSelectionChanged(e: any, item: any) {
    if (!e.event && e.type == undefined) return; // Ignore programmatic changes

    const itemValue = item[this.valueExpr];

    if (!this.multiple) {
      const alreadySelected = this.value().includes(itemValue);
      if (alreadySelected && !this.nullable) {
        // Force signal update to re-render checkbox back to selected state
        this.value.set([...this.value()]);
        return;
      }
      this.value.set(alreadySelected ? [] : [itemValue]);
      return;
    }

    const currentValues = this.value();
    const valuesArray = Array.isArray(currentValues) ? currentValues : [];

    let newValues: any[];
    if (valuesArray.includes(itemValue)) {
      newValues = valuesArray.filter(v => v !== itemValue);
    } else {
      newValues = [...valuesArray, itemValue];
    }

    this.value.set(newValues);
  }

  /**
   * The single-select path. A radio group cannot clear itself, so there is no unselect case
   * to handle here - which is the point of using one for mutually exclusive options.
   */
  onRadioSelectionChanged(e: any): void {
    if (!e.event && e.type == undefined) return; // Ignore programmatic changes
    if (e.value === null || e.value === undefined) return;

    this.value.set([e.value]);
  }

  toggleAll(e: any, selectAll: boolean): void {
    if (!e.event && e.type == undefined) return; // Ignore programmatic changes

    if (selectAll) {
      this.value.set(this.options().map(o => o[this.valueExpr]));
    } else {
      this.value.set([]);
    }
  }

  clear(): void {
    if (this.baselineValue !== null) {
      this.value.set(this.baselineValue);
    } else if (this.activeWhenNotEmpty) {
      this.value.set([]);
    } else {
      this.value.set(this.options().map(o => o[this.valueExpr]));
    }
  }
}
